import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { AUTH } from '../../constants/actionTypes.js';
// import lusukuLogo from 'assets/img/lusukuLogo517.png';
import lusukuLogo from 'assets/img/lusuku_logo_main.png';
import { resolveLandingRoute } from '../../utilities/landingRoute.js';

const API_URL = process.env.REACT_APP_SERVER_URL || '';

const PartnerActivate = () => {
    const history = useHistory();
    const location = useLocation();
    const dispatch = useDispatch();

    const [token, setToken] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [tokenValid, setTokenValid] = useState(true); // assume valid until proven otherwise

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const t = params.get('token');
        if (!t) {
            setTokenValid(false);
            setError('No activation token found in this link. Please use the link from your invitation email.');
        } else {
            setToken(t);
        }
    }, [location.search]);

    const validatePassword = () => {
        if (password.length < 8) return 'Password must be at least 8 characters.';
        if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter.';
        if (!/[0-9]/.test(password)) return 'Password must contain at least one number.';
        if (password !== confirmPassword) return 'Passwords do not match.';
        return null;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');

        const validationError = validatePassword();
        if (validationError) {
            setError(validationError);
            return;
        }

        setLoading(true);
        try {
            const response = await axios.post(`${API_URL}/api/3pl-partners/activate`, {
                token,
                password,
                confirmPassword,
            });

            if (response.data.success) {
                const { partner, token: jwtToken } = response.data.data;

                // Build redux profile from activation response
                const partnerProfile = {
                    result: {
                        _id: partner._id,
                        email: partner.email,
                        companyName: partner.companyName,
                        operationMode: '3PL_PARTNER',
                        role: '3PL_PARTNER',
                        partnerId: partner._id,
                        imageUrl: partner.logo?.url || null,
                        logo: partner.logo || null,
                    },
                    token: jwtToken,
                };

                dispatch({ type: AUTH, data: partnerProfile });
                toast.success(`Welcome to Lusuku, ${partner.companyName}!`);
                history.replace(resolveLandingRoute(partnerProfile.result));
            }
        } catch (err) {
            const msg = err.response?.data?.error?.message || 'Activation failed. Please try again or contact support.';
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    const strengthChecks = [
        { label: 'At least 8 characters', pass: password.length >= 8 },
        { label: 'One uppercase letter', pass: /[A-Z]/.test(password) },
        { label: 'One number', pass: /[0-9]/.test(password) },
        { label: 'Passwords match', pass: password && password === confirmPassword },
    ];

    return (
        <div style={{
            minHeight: '100vh',
            display: 'flex',
            background: 'linear-gradient(135deg, #011a03 0%, #013d06 55%, #006202 100%)',
            fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        }}>
            {/* Left branding panel */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '48px',
                color: '#fff',
                display: window.innerWidth < 768 ? 'none' : 'flex',
            }}>
                <img src={lusukuLogo} alt="Lusuku" style={{ width: 90, marginBottom: 24, borderRadius: 16 }} />
                <h1 style={{ fontSize: 32, fontWeight: 700, margin: '0 0 12px' }}>Lusuku Partners</h1>
                <p style={{ color: '#a8c0f0', fontSize: 16, textAlign: 'center', maxWidth: 320 }}>
                    The 3PL partner program. Manage riders, track deliveries, and grow your fleet.
                </p>
                <div style={{ marginTop: 40, width: '100%', maxWidth: 300 }}>
                    {['Manage your rider fleet', 'Track deliveries in real-time', 'Access earnings & analytics'].map((item, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                            <span style={{
                                width: 28, height: 28, borderRadius: '50%', background: 'rgba(255,255,255,0.18)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, flexShrink: 0
                            }}>✓</span>
                            <span style={{ color: '#c8d8f0', fontSize: 14 }}>{item}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Right form panel */}
            <div style={{
                width: '100%', maxWidth: 480,
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                padding: '48px 40px',
                boxShadow: '-8px 0 40px rgba(0,0,0,0.3)',
            }}>
                <div style={{ marginBottom: 32 }}>
                    <img src={lusukuLogo} alt="Lusuku" style={{ width: 48, marginBottom: 16, display: 'block' }} />
                    <h2 style={{ fontSize: 24, fontWeight: 700, color: '#0a0f2c', margin: '0 0 6px' }}>
                        Activate Your Account
                    </h2>
                    <p style={{ color: '#6B7280', fontSize: 14, margin: 0 }}>
                        Set a secure password to access your partner dashboard.
                    </p>
                </div>

                {!tokenValid ? (
                    <div style={{
                        background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 10,
                        padding: 20, color: '#991B1B', fontSize: 14, lineHeight: 1.6
                    }}>
                        <strong>⚠️ Invalid Activation Link</strong><br />
                        {error}<br /><br />
                        Contact <a href="mailto:support@lusuku.ug" style={{ color: '#023485' }}>support@lusuku.ug</a> to request a new link.
                    </div>
                ) : (
                    <form onSubmit={handleSubmit}>
                        {error && (
                            <div style={{
                                background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
                                padding: '12px 16px', color: '#991B1B', fontSize: 13, marginBottom: 20
                            }}>
                                {error}
                            </div>
                        )}

                        {/* Password */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                                New Password *
                            </label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    placeholder="Create a strong password"
                                    required
                                    style={{
                                        width: '100%', padding: '12px 44px 12px 14px', borderRadius: 8,
                                        border: '1.5px solid #D1D5DB', fontSize: 14, outline: 'none',
                                        boxSizing: 'border-box', color: '#111',
                                    }}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    style={{
                                        position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)',
                                        background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 16,
                                        display: 'flex', alignItems: 'center',
                                    }}
                                    title={showPassword ? 'Hide password' : 'Show password'}
                                >
                                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                                </button>
                            </div>
                        </div>

                        {/* Confirm password */}
                        <div style={{ marginBottom: 20 }}>
                            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                                Confirm Password *
                            </label>
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={e => setConfirmPassword(e.target.value)}
                                placeholder="Re-enter your password"
                                required
                                style={{
                                    width: '100%', padding: '12px 14px', borderRadius: 8,
                                    border: '1.5px solid #D1D5DB', fontSize: 14, outline: 'none',
                                    boxSizing: 'border-box', color: '#111',
                                }}
                            />
                        </div>

                        {/* Strength checklist */}
                        {password && (
                            <div style={{
                                background: '#F9FAFB', borderRadius: 8, padding: '12px 16px', marginBottom: 20
                            }}>
                                {strengthChecks.map((c, i) => (
                                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < 3 ? 6 : 0 }}>
                                        <span style={{ fontSize: 13, color: c.pass ? '#006202' : '#9CA3AF' }}>
                                            {c.pass ? '✓' : '○'}
                                        </span>
                                        <span style={{ fontSize: 13, color: c.pass ? '#374151' : '#9CA3AF' }}>{c.label}</span>
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading}
                            style={{
                                width: '100%', padding: '14px', borderRadius: 8, border: 'none',
                                background: loading ? '#9CA3AF' : '#006202',
                                color: '#fff', fontSize: 15, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                                transition: 'background 0.2s',
                            }}
                        >
                            {loading ? (
                                <>
                                    <Spinner animation="border" size="sm" />
                                    Activating...
                                </>
                            ) : (
                                'Activate Account & Sign In →'
                            )}
                        </button>

                        <p style={{ textAlign: 'center', fontSize: 12, color: '#9CA3AF', marginTop: 20 }}>
                            © {new Date().getFullYear()} Lusuku Fresh Services. All rights reserved.
                        </p>
                    </form>
                )}
            </div>
        </div>
    );
};

export default PartnerActivate;
